import { channelHeaders } from './channel.js';
import { errorFromResponse } from './errors.js';
import { appendCursorParams } from './pagination.js';
export class MiraApiClient {
    baseUrl;
    channel;
    tokenStore;
    fetchImpl;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/u, '');
        this.channel = options.channel;
        this.tokenStore = options.tokenStore;
        this.fetchImpl = options.fetchImpl ?? (globalThis.fetch.bind(globalThis));
    }
    chatSocketUrl(conversationId, token) {
        const baseUrl = new URL(this.baseUrl, globalThis.location?.origin ?? 'http://localhost');
        baseUrl.protocol = baseUrl.protocol === 'https:' ? 'wss:' : 'ws:';
        const base = baseUrl.toString().replace(/\/$/u, '');
        return `${base}/chat/${encodeURIComponent(conversationId)}?token=${encodeURIComponent(token)}`;
    }
    async request(path, options = {}) {
        const token = await this.tokenStore?.getToken();
        const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
            ...options,
            headers: {
                'content-type': 'application/json',
                ...channelHeaders({ ...this.channel, outputLocale: options.outputLocale ?? this.channel.outputLocale }),
                ...(token ? { authorization: `Bearer ${token}` } : {}),
            },
            body: options.body === undefined ? undefined : JSON.stringify(options.body),
        });
        if (response.status === 204) {
            return undefined;
        }
        if (!response.ok) {
            throw await errorFromResponse(response);
        }
        return (await response.json());
    }
    async register(username, password) {
        const response = await this.request('/auth/register', { method: 'POST', body: { username, password } });
        await this.tokenStore?.setToken(response.token);
        return response;
    }
    async login(username, password) {
        const response = await this.request('/auth/login', { method: 'POST', body: { username, password } });
        await this.tokenStore?.setToken(response.token);
        return response;
    }
    async loginWithWechatCode(code) {
        const response = await this.request('/auth/wechat-mini/login', { method: 'POST', body: { code } });
        await this.tokenStore?.setToken(response.token);
        return response;
    }
    async logout() {
        await this.request('/auth/logout', { method: 'POST' });
        await this.tokenStore?.setToken(null);
    }
    me = () => this.request('/me');
    changePassword = (body) => this.request('/auth/change-password', { method: 'POST', body });
    updateProfile = (body) => this.request('/me/profile', { method: 'PUT', body });
    updateBirthProfile = (body) => this.request('/me/birth-profile', { method: 'PUT', body });
    home = () => this.request('/home');
    recentConversations = (query) => this.request(appendCursorParams('/conversations/recent', query));
    createConversation = (body) => this.request('/conversations', { method: 'POST', body });
    conversationMessages = (conversationId, query) => this.request(appendCursorParams(`/conversations/${encodeURIComponent(conversationId)}/messages`, query));
    sendMessage = (conversationId, content, options = {}) => this.request(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
        method: 'POST',
        body: {
            content,
            outputLocale: options.outputLocale,
            clientMessageId: options.clientMessageId,
            sourceMaterialIds: options.sourceMaterialIds,
        },
        outputLocale: options.outputLocale,
    });
    retryAssistantResponse = (conversationId) => this.request(`/conversations/${encodeURIComponent(conversationId)}/assistant-retry`, { method: 'POST' });
    topics = (query) => this.request(appendCursorParams('/topics', query));
    topic = (topicId) => this.request(`/topics/${encodeURIComponent(topicId)}`);
    continueTopic = (topicId) => this.request(`/topics/${encodeURIComponent(topicId)}/continue`, { method: 'POST' });
    deleteTopic = (topicId) => this.request(`/topics/${encodeURIComponent(topicId)}`, { method: 'DELETE' });
    relationships = (query) => this.request(appendCursorParams('/relationships', query));
    createRelationship = (body) => this.request('/relationships', { method: 'POST', body });
    relationship = (relationshipId) => this.request(`/relationships/${encodeURIComponent(relationshipId)}`);
    updateRelationship = (relationshipId, body) => this.request(`/relationships/${encodeURIComponent(relationshipId)}`, {
        method: 'PATCH',
        body,
    });
    deleteRelationship = (relationshipId) => this.request(`/relationships/${encodeURIComponent(relationshipId)}`, { method: 'DELETE' });
    relationshipChat = (relationshipId) => this.request(`/relationships/${encodeURIComponent(relationshipId)}/chat`, { method: 'POST' });
    createRelationshipAlias = (relationshipId, body) => this.request(`/relationships/${encodeURIComponent(relationshipId)}/aliases`, { method: 'POST', body });
    createRelationshipSocialIdentity = (relationshipId, body) => this.request(`/relationships/${encodeURIComponent(relationshipId)}/social-identities`, {
        method: 'POST',
        body,
    });
    acceptRelationshipSuggestion = (suggestionId) => this.request(`/relationships/suggestions/${encodeURIComponent(suggestionId)}/accept`, { method: 'POST' });
    chooseRelationshipSuggestion = (suggestionId, body) => this.request(`/relationships/suggestions/${encodeURIComponent(suggestionId)}/choose`, {
        method: 'POST',
        body,
    });
    dismissRelationshipSuggestion = (suggestionId) => this.request(`/relationships/suggestions/${encodeURIComponent(suggestionId)}/dismiss`, { method: 'POST' });
    memoryItems = (query) => {
        const path = appendCursorParams('/memory-items', query);
        const url = new URL(path, 'https://mira.local');
        if (query?.status)
            url.searchParams.set('status', query.status);
        return this.request(`${url.pathname}${url.search}`);
    };
    confirmMemoryItem = (id) => this.request(`/memory-items/${encodeURIComponent(id)}/confirm`, { method: 'POST' });
    deleteMemoryItem = (id) => this.request(`/memory-items/${encodeURIComponent(id)}`, { method: 'DELETE' });
    insights = (query) => this.request(appendCursorParams('/insights', query));
    topicInsights = (topicId, query) => {
        const path = appendCursorParams('/insights', query);
        const separator = path.includes('?') ? '&' : '?';
        return this.request(`${path}${separator}topicId=${encodeURIComponent(topicId)}`);
    };
    continueInsight = (id) => this.request(`/insights/${encodeURIComponent(id)}/continue`, { method: 'POST' });
    deleteInsight = (id) => this.request(`/insights/${encodeURIComponent(id)}`, { method: 'DELETE' });
    notes = (query) => this.request(appendCursorParams('/notes', query));
    note = (id) => this.request(`/notes/${encodeURIComponent(id)}`);
    createNote = (body) => this.request('/notes', { method: 'POST', body });
    updateNote = (id, body) => this.request(`/notes/${encodeURIComponent(id)}`, { method: 'PATCH', body });
    deleteNote = (id) => this.request(`/notes/${encodeURIComponent(id)}`, { method: 'DELETE' });
    assetUploadUrl = (body) => this.request('/assets/upload-url', { method: 'POST', body });
    completeAssetUpload = (assetId) => this.request(`/assets/${encodeURIComponent(assetId)}/complete`, { method: 'POST' });
    assetReadUrl = (assetId) => this.request(`/assets/${encodeURIComponent(assetId)}/read-url`);
    sourceMaterialUploadUrl = (body) => this.request('/source-materials/upload-url', { method: 'POST', body });
    sourceMaterial = (sourceId) => this.request(`/source-materials/${encodeURIComponent(sourceId)}`);
    startSourceMaterialProcessing = (sourceId) => this.request(`/source-materials/${encodeURIComponent(sourceId)}/processing`, { method: 'POST' });
    completeSourceMaterial = (sourceId, body) => this.request(`/source-materials/${encodeURIComponent(sourceId)}/complete`, { method: 'POST', body });
    failSourceMaterial = (sourceId, failureCode) => this.request(`/source-materials/${encodeURIComponent(sourceId)}/fail`, {
        method: 'POST',
        body: { failureCode },
    });
    correctSourceMaterialTranscript = (sourceId, body) => this.request(`/source-materials/${encodeURIComponent(sourceId)}/transcript`, {
        method: 'PATCH',
        body,
    });
    sourceMaterialReadUrl = (sourceId) => this.request(`/source-materials/${encodeURIComponent(sourceId)}/read-url`);
    deleteSourceMaterial = (sourceId) => this.request(`/source-materials/${encodeURIComponent(sourceId)}`, { method: 'DELETE' });
    billingProducts = () => this.request('/billing/products');
    createBillingOrder = (body) => this.request('/billing/orders', { method: 'POST', body });
    billingOrder = (orderId) => this.request(`/billing/orders/${encodeURIComponent(orderId)}`);
    completeFakeBillingOrder = (orderId) => this.request(`/billing/orders/${encodeURIComponent(orderId)}/fake-complete`, { method: 'POST' });
    failFakeBillingOrder = (orderId) => this.request(`/billing/orders/${encodeURIComponent(orderId)}/fake-fail`, { method: 'POST' });
    billingManagement = () => this.request('/billing/management');
    entitlements = () => this.request('/entitlements');
    designAssets = () => this.request('/design-assets/catalog');
    designAsset = (key) => this.request(`/design-assets/${encodeURIComponent(key)}`);
}
